import { FC, useState, cloneElement, ReactElement, useRef, useEffect } from 'react'
import { Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import clsx from 'clsx'
import { useMobileDetector } from '@hooks/use-mobile-detector'

import { Swiper as SwiperTypes } from 'swiper/types'
import 'swiper/css'
import style from './index.module.scss'

type SlidingTabContentProps<T = {}> = {
  data: T[]
  boxClassName?: string
  navigationBoxClassName?: string
  dragDisablePointerEvents?: boolean // 处理拖拽时，iframe独立文档上下文导致的鼠标事件无法正确冒泡，导致拖拽状态未被释放。
  children: ReactElement
}

const SlidingTabContent: FC<SlidingTabContentProps> = ({
  data,
  boxClassName,
  navigationBoxClassName,
  dragDisablePointerEvents,
  children,
}) => {
  const isMobileDevice = useMobileDetector()
  const isEndRef = useRef<boolean>(false)
  const isBeginningRef = useRef<boolean>(true)
  const [swiperInstance, setSwiperInstance] = useState<SwiperTypes | null>(null)
  const [isSinglePage, setIsSinglePage] = useState<boolean>(false)
  const [slideIsBeginningOrIsEnd, setSlideIsBeginningOrIsEnd] = useState<{
    isBeginning: boolean
    isEnd: boolean
  }>({
    isBeginning: true,
    isEnd: false,
  })

  const onChangeSlide = (swiper: SwiperTypes) => {
    setSlideIsBeginningOrIsEnd((prev) => ({
      ...prev,
      isBeginning: swiper.isBeginning,
      isEnd: swiper.isEnd,
    }))
  }

  // 处理swiper isBeginning，isEnd显示不准确问题
  // 升级swiper大版本可能解决该问题
  const handleProgress = (swiper: SwiperTypes, progress: number) => {
    // @ts-ignore
    if (swiper?._progressBandAid) return

    const isBeginning = Number(progress.toFixed(2)) < 0.01
    const isEnd = Number(progress.toFixed(2)) > 0.99

    isEndRef.current = isEnd
    isBeginningRef.current = isBeginning

    setSlideIsBeginningOrIsEnd((prev) => ({
      ...prev,
      isBeginning: swiper.isBeginning,
      isEnd: swiper.isEnd,
    }))
  }

  const widthHandle = (swiper: SwiperTypes) => {
    const spaceBetween: number = swiper?.params?.spaceBetween || 0
    // @ts-ignore
    const allWidth = ((swiper?.slidesSizesGrid as number[]) || []).reduce(
      (sum, num) => sum + num + spaceBetween,
      -spaceBetween
    )

    if (allWidth < swiper.width) {
      setIsSinglePage(true)
    } else {
      setIsSinglePage(false)
    }
  }

  const onSwiperInit = (swiper: SwiperTypes) => {
    swiper.on('slideChange', onChangeSlide)

    widthHandle(swiper)
    setSwiperInstance(swiper)
  }

  useEffect(() => {
    if (!swiperInstance) return

    widthHandle(swiperInstance)
  }, [data])

  if (!children || Object.prototype.toString.call(data) !== '[object Array]') {
    return <></>
  }

  return (
    <div className={clsx(boxClassName, 'relative', 'w-auto h-full box-content')}>
      <Swiper
        // autoplay={{
        //   delay: 3000,
        //   disableOnInteraction: false,
        // }}
        observer={true}
        observeParents={true}
        className={clsx(style['swiper-wrapper'], '!overflow-visible')}
        loop={false}
        autoplay={false}
        slidesPerGroup={1}
        grabCursor
        slidesPerView={'auto'}
        spaceBetween={isMobileDevice ? 12 : 16}
        onInit={onSwiperInit}
        modules={[Navigation]}
        scrollbar={{ draggable: true }}
        navigation={{
          nextEl: '.pd-display-swiper-next',
          prevEl: '.pd-display-swiper-prev',
        }}
        // slidesOffsetBefore={isMobileDevice ? 0 : (innerWidth - 1200) / 2}
        onProgress={handleProgress}
        onSliderMove={() => {
          dragDisablePointerEvents &&
            !!document &&
            document?.querySelectorAll('iframe').forEach((iframe) => {
              iframe.style.pointerEvents = 'none'
            })
        }}
        onTouchEnd={() => {
          dragDisablePointerEvents &&
            !!document &&
            document.querySelectorAll('iframe').forEach((iframe) => {
              iframe.style.pointerEvents = 'auto'
            })
        }}
      >
        {data?.map((value, index) => (
          <SwiperSlide key={index} className="!h-auto !w-fit">
            {!!children && cloneElement(children, { ...value })}
          </SwiperSlide>
        ))}
      </Swiper>

      {!isSinglePage && (
        <div
          className={clsx(
            navigationBoxClassName,
            'flex flex-row-reverse flex-grow-0 z-10',
            'w-full mt-24px h-fit gap-4px',
            'laptop:gap-8px'
          )}
        >
          <div
            className={clsx(
              '.pd-display-swiper-next',
              'block bg-center',
              'h-36px w-36px',
              'laptop:h-48px laptop:w-48px',
              'bg-iconRightArrow bg-no-repeat',
              // 'hover:bg-none',
              {
                // 'opacity-90': !slideIsBeginningOrIsEnd?.isEnd,
                // 'opacity-26 cursor-not-allowed': slideIsBeginningOrIsEnd?.isEnd,
                'opacity-90 cursor-pointer': !isEndRef.current,
                'opacity-26 cursor-not-allowed': isEndRef.current,
              }
            )}
            onClick={() => {
              if (isEndRef.current) {
                return
              }

              swiperInstance?.slideNext()
            }}
          />
          <div
            className={clsx(
              '.pd-display-swiper-prev',
              'block bg-center',
              'h-36px w-36px',
              'laptop:h-48px laptop:w-48px',
              'rotate-180',
              'bg-iconRightArrow bg-no-repeat',
              // 'hover:bg-none',
              {
                // 'opacity-90': !slideIsBeginningOrIsEnd?.isBeginning,
                // 'opacity-26 cursor-not-allowed': slideIsBeginningOrIsEnd?.isBeginning,
                'opacity-90 cursor-pointer': !isBeginningRef.current,
                'opacity-26 cursor-not-allowed': isBeginningRef.current,
              }
            )}
            onClick={() => {
              if (isBeginningRef.current) {
                return
              }

              swiperInstance?.slidePrev()
            }}
          />
        </div>
      )}
    </div>
  )
}

export type { SlidingTabContentProps }
export default SlidingTabContent
